import { useIsOffline } from 'components/common/withIsOffline';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import React, { ReactElement, memo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { ChartFiltersUnion } from 'shared/types/analytics';
import useIsMobile from '../../../hooks/useIsMobile';
import { useChartRuntime } from 'charts/hooks/useChartRuntime';
import { MemoFiltersRendererPresentational } from './presentational';
import { useChartAvailableFieldsSet } from './useChartAvailableFieldsSet';
import type { FiltersControlsRendererProps, FiltersRendererProps } from './types';

const MemoControlsRenderer = memo(ControlsRenderer);

export function FiltersRenderer({ id }: FiltersRendererProps): ReactElement {
  const chartContext = useChartRuntime(id);
  const filters = chartContext?.filters;
  const filterTypes = filters && Object.keys(filters);

  if (!chartContext || !filterTypes || !filters) {
    return <></>;
  }

  return <MemoControlsRenderer chartContext={chartContext} />;
}

/**
 * The filters of a chart in its details. A chosen value is applied at once
 * (PT-6804: no apply button); it overrides the section filters for this chart.
 */
function ControlsRenderer({
  chartContext,
}: FiltersControlsRendererProps): ReactElement {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const isOffline = useIsOffline();
  const [tabIndex, setTabIndex] = useState(0);

  const filters: ChartFiltersUnion = chartContext.filters!;
  const filterTypes = Object.keys(filters) as (keyof ChartFiltersUnion)[];
  const availableFieldsSet = useChartAvailableFieldsSet();

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number): void => {
      setTabIndex(newValue);
    },
    []
  );

  const formatFilterLabel = useCallback(
    (labelId: string) => formatTranslation(intl, unsafeTranslationKey(labelId)),
    [intl]
  );

  const formatEmptyLabel = useCallback(
    () => formatTranslation(intl, unsafeTranslationKey('none')),
    [intl]
  );

  return (
    <MemoFiltersRendererPresentational
      isMobile={isMobile}
      filtersDisabled={Boolean(chartContext.filtersDisabledOffline && isOffline)}
      filterTypes={filterTypes}
      filters={filters}
      tabIndex={tabIndex}
      availableFieldsSet={availableFieldsSet}
      onTabChange={handleTabChange}
      formatFilterLabel={formatFilterLabel}
      formatEmptyLabel={formatEmptyLabel}
    />
  );
}
